import React from 'react';
import { motion } from 'framer-motion';
import { Globe, User, Server, Zap } from 'lucide-react';

const RoutingLogic = ({ isDarkMode }) => {
  return (
    <div className={`mt-20 p-8 rounded-3xl border relative overflow-hidden ${isDarkMode ? 'border-slate-800 bg-slate-900/20' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
        <div className="max-w-xl text-left">
          <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Local Peering</h3>
          <p className={`mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            EverestCloud is directly peered with NPIX (Nepal Internet Exchange). When a local user visits your site, the packet never leaves the country. Standard clouds force your traffic through undersea cables to Mumbai or Singapore.
          </p>
        </div>
        <div className="flex gap-4">
           <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-500 text-xs font-bold">
             PATH: DIRECT PEERING
           </div>
           <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-500 text-xs font-bold">
             HOP COUNT: 2-3 (Local)
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 relative">
        {/* Local Path */}
        <div className="space-y-4">
           <div className="flex items-center gap-2 text-xs font-black text-emerald-500 uppercase">
             <Zap size={14}/> EverestCloud Path (Nepal)
           </div>
           <div className={`h-48 rounded-2xl border p-6 flex flex-col justify-between relative overflow-hidden ${isDarkMode ? 'bg-black/40 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>

              <div className="flex items-center justify-between z-10 mt-10">
                  <div className="flex flex-col items-center gap-2">
                     <div className={`p-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
                        <User className={isDarkMode ? 'text-white' : 'text-slate-700'} size={20} />
                     </div>
                     <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>USER IN NEPAL</span>
                  </div>

                  <div className="flex-1 flex justify-center px-4">
                     <div className="h-[2px] w-full bg-emerald-500/20 relative">
                        <motion.div
                          animate={{ left: ['0%', '100%'] }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="absolute top-1/2 -translate-y-1/2 w-8 h-4 bg-emerald-500/40 blur-md rounded-full"
                        />
                        <motion.div
                          animate={{ left: ['0%', '100%'] }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-500 rounded-full"
                        />
                     </div>
                  </div>

                  <div className="flex flex-col items-center gap-2">
                     <div className={`p-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
                        <Server className="text-emerald-500" size={20} />
                     </div>
                     <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>EVERESTCLOUD</span>
                  </div>
              </div>

              {/* Marquee Text */}
              <div className="w-full h-8 bg-emerald-500/10 border-t border-emerald-500/10 flex items-center overflow-hidden absolute bottom-0 left-0 right-0">
                <motion.div
                  animate={{ x: ["100%", "-100%"] }}
                  transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                  className="whitespace-nowrap text-[10px] font-mono font-bold text-emerald-500 px-4"
                >
                  PEERING_STATUS: ACTIVE | NPIX_GIGABIT_ETH | LATENCY_&lt;10ms | ZERO_PACKET_LOSS
                </motion.div>
              </div>
           </div>
           <div className="flex justify-between text-[10px] font-bold opacity-50">
             <span>RTT: &lt;10ms</span>
             <span>HOP: NPIX Peering</span>
           </div>
        </div>

        {/* Foreign Path */}
        <div className="space-y-4 opacity-50 grayscale transition-all hover:grayscale-0 hover:opacity-100">
           <div className="flex items-center gap-2 text-xs font-black text-slate-500 uppercase">
             <Globe size={14}/> Foreign Cloud Path (India/Singapore)
           </div>
           <div className={`h-48 rounded-2xl border p-6 flex flex-col justify-between relative overflow-hidden ${isDarkMode ? 'bg-black/20 border-slate-800' : 'bg-slate-100 border-slate-200'}`}>

              <div className="flex items-center justify-between z-10 mt-10">
                  <div className="flex flex-col items-center gap-2">
                     <div className={`p-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
                        <User className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} size={20} />
                     </div>
                     <span className="text-[10px] font-bold">USER IN NEPAL</span>
                  </div>

                  <div className="flex-1 flex justify-center px-4 relative">
                     <div className="h-[2px] w-full bg-slate-500/20 relative flex items-center">
                        <motion.div
                          animate={{ left: ['0%', '100%'] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                          className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-500 rounded-full"
                        />
                     </div>
                     {/* Slow Icon - Aligned properly above the line, no badge box */}
                     <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[8px] font-bold text-slate-500 whitespace-nowrap">
                        LATENCY SPIKE
                     </div>
                  </div>

                  <div className="flex flex-col items-center gap-2">
                     <div className={`p-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
                        <Globe className="text-slate-500" size={20} />
                     </div>
                     <span className="text-[10px] font-bold">FOREIGN REGION</span>
                  </div>
              </div>

           </div>
           <div className="flex justify-between text-[10px] font-bold opacity-50">
             <span>RTT: 85ms+</span>
             <span>PATH: Undersea Fiber / BGP Transit</span>
           </div>
        </div>
      </div>
    </div>
  );
};

export default RoutingLogic;
